import React from 'react';

import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { CheckIcon } from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  FadeIn,
  Footer,
  GradientBg,
  Header,
  InsuranceCarousel,
  InsuranceTabs,
  PageHeader
} from '../components';
import { ecommerce } from '../constants/insuranceTypes';

const reasons = [
  {
    name: 'Loss Prevention',
    description: 'Lost, stolen, or damaged property and inventory'
  },
  {
    name: 'Lawsuit',
    description: 'Reputational damage and customer lawsuit'
  },
  {
    name: 'Accidents',
    description: 'Employees accidents and injuries'
  },
  {
    name: 'Disruption',
    description: 'Business interruption and lost income'
  },
  {
    name: 'Defects',
    description: 'Defective or malfunctioning products'
  },
  {
    name: 'Online Presence',
    description: 'Cyber security'
  }
];

const types = Object.keys(ecommerce).map((type: string) => ({
  title: type,
  icon: ecommerce[type]
}));

const Ecommerce: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="E-commerce"
        description="E-commerce insurance for online sellers: liability, cyber, and inventory. Protect your store with coverage built for marketplaces and DTC brands. — Coverdash"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'E-commerce',
          description:
            'E-commerce insurance for online sellers: liability, cyber, and inventory. Protect your store with coverage built for marketplaces and DTC brands. — Coverdash'
        }}
      />
      <div className="overflow-hidden">
        <GradientBg />
        <Header />

        {/* Hero */}
        <PageHeader
          title="E-commerce merchant"
          description="Running your own business comes with plenty of achievements, but every business also has its own risks."
          image={
            <img
              src="/img/pages/ecommerce.png"
              alt="E-commerce hero image"
              aria-hidden="true"
              className="mx-auto max-w-2xl w-full mt-8 md:mt-0"
            />
          }
        />

        {/* Section-1 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:px-8 lg:grid lg:grid-cols-5 lg:gap-x-8">
          <div className="lg:col-span-2 text-center lg:text-left">
            <FadeIn>
              <p className="text-base font-semibold text-primary uppercase tracking-wide">
                Everything you need
              </p>
            </FadeIn>
            <FadeIn>
              <h2 className="mt-2 text-5xl font-extrabold text-alt">
                Why do <span className="text-primary">e-commerce</span>{' '}
                merchants need insurance?
              </h2>
            </FadeIn>

            <p className="mt-4 text-lg text-gray-500">
              E-commerce businesses are often left in the dark when it comes to
              protecting what they have built. No matter what type of e-commerce
              business you operate, each one comes with a particular set of
              common risks. Obtaining the right insurance is crucial to protect
              your business from:
            </p>
          </div>
          <div className="mt-12 lg:mt-0 lg:col-span-3">
            <dl className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col sm:gap-x-6 sm:gap-y-10 lg:gap-x-8">
              {reasons.map(reason => (
                <div key={reason.name} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-medium text-gray-900">
                      {reason.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-500">
                    {reason.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Section-2 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What does{' '}
              <span className="text-primary">e-commerce merchant</span>{' '}
              insurance cover{' '}
            </h2>
          </FadeIn>

          <div className="max-w-2xl mx-auto text-xl text-gray-500">
            <p className="mt-6">
              Choosing the right e-commerce business insurance can help protect
              you from costly risks and liabilities, but the right policies for
              your business will depend on the services you offer.
            </p>
            <p className="mt-4">
              Here are some of the most popular insurance policies for
              e-commerce businesses, covering the most common risks you&apos;ll
              face as an e-commerce business owner:
            </p>
          </div>

          <div className="mt-12">
            <InsuranceTabs />
          </div>
        </div>

        {/* Section-3 */}
        <div className="py-8 lg:py-20 text-center">
          <FadeIn>
            <div className="mx-auto max-w-3xl lg:max-w-7xl px-4 sm:px-6 lg:px-8">
              <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold  text-alt">
                What types of{' '}
                <span className="text-primary">e-commerce merchant</span> do we
                cover?
              </h2>
            </div>
          </FadeIn>
          <div className="mt-12 px-2 md:px-12 sm:px-0">
            <InsuranceCarousel items={types} />
          </div>
        </div>

        {/* Section-4 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl py-8 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <FadeIn>
                <h2 className="mt-2 text-5xl font-extrabold  text-alt text-center sm:text-left">
                  How much does{' '}
                  <span className="text-primary">
                    e-commerce merchant insurance
                  </span>{' '}
                  cost?{' '}
                </h2>
              </FadeIn>
              <div className="mt-4 text-lg text-gray-500 text-center sm:text-left">
                <p>
                  Coverdash helps you identify the appropriate coverage to suit
                  your e-commerce business needs without breaking the bank. The
                  exact amount you pay will depend on:
                </p>

                <ul className="list-disc p-5 text-left">
                  <li>The type of e-commerce business you operate</li>
                  <li>Your revenue and employee payroll</li>
                  <li>Your number of employees</li>
                  <li>Your business location</li>
                </ul>
              </div>
            </div>
            <div className="justify-right items-right">
              <img
                src="/img/insurance-cost.jpg"
                className="h-full w-full lg:w-[140%]"
                alt="illustration of woman pointing at infographic"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <BottomGraphic />
        <Footer />
      </div>
    </>
  );
};

export default Ecommerce;
